import { useEffect, useState } from "react";
import { Type, Contrast, PauseCircle, RotateCcw } from "lucide-react";

const SIZES = [
  { id: "base", label: "A", cls: "", aria: "Default text size" },
  { id: "lg", label: "A+", cls: "text-lg-scale", aria: "Larger text" },
  { id: "xl", label: "A++", cls: "text-xl-scale", aria: "Largest text" },
];

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("a11y-prefs")) || {};
  } catch (_) {
    return {};
  }
};

export function applyPrefs(p) {
  const h = document.documentElement;
  SIZES.forEach((s) => s.cls && h.classList.remove(s.cls));
  const size = SIZES.find((s) => s.id === p.size);
  if (size?.cls) h.classList.add(size.cls);
  h.classList.toggle("hc", !!p.hc);
  h.classList.toggle("reduce-motion", !!p.motion);
}

export default function A11yToolbar() {
  const [p, setP] = useState(load);
  useEffect(() => {
    applyPrefs(p);
    localStorage.setItem("a11y-prefs", JSON.stringify(p));
  }, [p]);

  const toggleCls = (on) =>
    `inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors ${on ? "bg-on-teal text-teal" : "text-on-teal hover:bg-on-teal/15"}`;

  return (
    <div className="bg-teal-dark text-on-teal" role="region" aria-label="Accessibility options" data-testid="a11y-toolbar">
      <div className="container-x flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
        <span className="flex items-center gap-1.5 text-sm font-semibold"><Type size={16} aria-hidden="true" />Text size</span>
        <div className="flex gap-1" role="group" aria-label="Text size">
          {SIZES.map((s) => (
            <button key={s.id} type="button" aria-pressed={(p.size || "base") === s.id} aria-label={s.aria}
              onClick={() => setP({ ...p, size: s.id })} className={toggleCls((p.size || "base") === s.id)}
              data-testid={`a11y-text-${s.id}`}>{s.label}</button>
          ))}
        </div>
        <button type="button" aria-pressed={!!p.hc} onClick={() => setP({ ...p, hc: !p.hc })} className={toggleCls(p.hc)} data-testid="a11y-high-contrast">
          <Contrast size={16} aria-hidden="true" />High contrast
        </button>
        <button type="button" aria-pressed={!!p.motion} onClick={() => setP({ ...p, motion: !p.motion })} className={toggleCls(p.motion)} data-testid="a11y-reduce-motion">
          <PauseCircle size={16} aria-hidden="true" />Reduce motion
        </button>
        {(p.size && p.size !== "base") || p.hc || p.motion ? (
          <button type="button" onClick={() => setP({})} className={toggleCls(false)} data-testid="a11y-reset">
            <RotateCcw size={16} aria-hidden="true" />Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
